import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronLeft, X } from "lucide-react";

const DEFAULT_TOUR_KEY = "pitch_perfect_tour_seen";

export function hasSeenTour(tourKey = DEFAULT_TOUR_KEY) {
  return localStorage.getItem(tourKey) === "true";
}

export function resetTour(tourKey) {
  if (tourKey) {
    localStorage.removeItem(tourKey);
  } else {
    // Reset all tours
    localStorage.removeItem(DEFAULT_TOUR_KEY);
    localStorage.removeItem("pitch_perfect_roleplay_tour_seen");
  }
}

export function markTourSeen(tourKey = DEFAULT_TOUR_KEY) {
  localStorage.setItem(tourKey, "true");
}

export default function GuidedTour({ steps, onComplete, isOpen, tourKey = DEFAULT_TOUR_KEY, onBeforeStep }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const tooltipRef = useRef(null);

  const step = steps[currentStep];

  const updatePosition = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (!el) {
      setTargetRect(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setTargetRect(rect);
  }, [step]);

  // Position tooltip after targetRect updates
  useEffect(() => {
    if (!targetRect || !tooltipRef.current) return;

    const tooltip = tooltipRef.current;
    const tooltipRect = tooltip.getBoundingClientRect();
    const pad = 16;
    const pos = step?.position || "bottom";
    let top, left;

    if (pos === "bottom") {
      top = targetRect.bottom + pad;
      left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
    } else if (pos === "top") {
      top = targetRect.top - tooltipRect.height - pad;
      left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
    } else if (pos === "right") {
      top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
      left = targetRect.right + pad;
    } else if (pos === "left") {
      top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
      left = targetRect.left - tooltipRect.width - pad;
    }

    // Clamp to viewport
    left = Math.max(pad, Math.min(left, window.innerWidth - tooltipRect.width - pad));
    top = Math.max(pad, Math.min(top, window.innerHeight - tooltipRect.height - pad));

    setTooltipStyle({ top: `${top}px`, left: `${left}px` });
  }, [targetRect, step?.position]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [isOpen, updatePosition]);

  // Run onBeforeStep and scroll target into view
  useEffect(() => {
    if (!isOpen || !step) return;

    const setup = async () => {
      // Run pre-step action (e.g., click a tab)
      if (onBeforeStep) {
        await onBeforeStep(currentStep, step);
      }
      // Small delay for DOM to update after tab switch
      await new Promise(r => setTimeout(r, 150));

      const el = document.querySelector(step.target);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        setTimeout(updatePosition, 350);
      } else {
        updatePosition();
      }
    };
    setup();
  }, [isOpen, currentStep, step, updatePosition, onBeforeStep]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleFinish = () => {
    markTourSeen(tourKey);
    setCurrentStep(0);
    onComplete?.();
  };

  if (!isOpen || !step) return null;

  const spotlightPad = 8;

  return createPortal(
    <div className="fixed inset-0 z-[9999]" data-testid="guided-tour-overlay">
      {/* Dark overlay with spotlight cutout via box-shadow */}
      {targetRect && (
        <div
          className="fixed inset-0 transition-all duration-300"
          style={{
            boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.65)`,
            top: targetRect.top - spotlightPad,
            left: targetRect.left - spotlightPad,
            width: targetRect.width + spotlightPad * 2,
            height: targetRect.height + spotlightPad * 2,
            borderRadius: "12px",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Click barrier (prevents interacting with background) */}
      <div className="fixed inset-0" onClick={(e) => e.stopPropagation()} />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-[10000] w-[340px] bg-card border border-border rounded-xl shadow-2xl p-5 transition-all duration-300 animate-in fade-in-0 slide-in-from-bottom-2"
        style={tooltipStyle}
        data-testid="tour-tooltip"
      >
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentStep
                    ? "w-6 bg-primary"
                    : i < currentStep
                    ? "w-1.5 bg-primary/50"
                    : "w-1.5 bg-muted-foreground/20"
                }`}
              />
            ))}
          </div>
          <span className="text-xs text-muted-foreground">
            {currentStep + 1}/{steps.length}
          </span>
        </div>

        {/* Icon + Content */}
        {step.icon && (
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
            <step.icon className="w-5 h-5 text-primary" />
          </div>
        )}
        <h3 className="font-heading font-semibold text-base mb-1" data-testid="tour-step-title">
          {step.title}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
          {step.description}
        </p>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleFinish}
            className="text-muted-foreground text-xs"
            data-testid="tour-skip-btn"
          >
            <X className="w-3 h-3 mr-1" /> Skip Tour
          </Button>
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrev}
                data-testid="tour-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleNext}
              className="bg-primary hover:bg-primary/90"
              data-testid="tour-next-btn"
            >
              {currentStep === steps.length - 1 ? "Got It!" : "Next"}
              {currentStep < steps.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
